/**
 * Die Nutzer des aktuellen Mandanten.
 *
 * Zwei Dinge haengen daran: die Auswahl bei der Zuweisung und die Anzeige
 * eines Namens statt einer Kennung. Das zweite ist kein Beiwerk -- eine
 * Ticketliste, in der unter "Bearbeiter" eine UUID steht, ist an dieser
 * Stelle unbrauchbar.
 *
 * Die Liste aendert sich selten und wird deshalb laenger gehalten. Sie ist
 * ausserdem klein: Ein Mandant hat eine Handvoll Bearbeiter, keine tausend.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { components } from '../../api/schema';
import { request } from '../../lib/apiClient';
import { ApiError } from '../../lib/apiError';
import { resetMfaFor } from '../auth/api';

export type AssignableUser = components['schemas']['AssignableUser'];
export type AgentCreated = components['schemas']['AgentCreated'];
export type PasswordReset = components['schemas']['PasswordReset'];

export const userKeys = { list: ['users'] as const };

/** Fuenf Minuten. Ein neuer Kollege kommt nicht im Minutentakt dazu. */
const USERS_STALE_TIME = 5 * 60 * 1000;

export function useAssignableUsers() {
  return useQuery({
    queryKey: userKeys.list,
    queryFn: () => request<AssignableUser[]>('/users'),
    staleTime: USERS_STALE_TIME,
    // Ein 403 bedeutet: Diese Rolle darf die Liste nicht sehen. Ein erneuter
    // Versuch aendert daran nichts.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.isPermanent) && failureCount < 2,
  });
}

/**
 * Loest eine Nutzerkennung in einen Namen auf.
 *
 * Ohne Treffer bleibt die Kennung stehen. Sie zu verschweigen waere
 * schlechter: Ein leeres Feld liest sich wie "niemand zugewiesen", und das
 * waere an dieser Stelle falsch.
 */
export function nameOf(users: AssignableUser[] | undefined, userId: string | null): string | null {
  if (!userId) return null;
  return users?.find((user) => user.id === userId)?.full_name ?? userId;
}

/**
 * Setzt den zweiten Faktor eines Benutzers zurueck -- F5-16.
 *
 * Die Liste wird danach neu geholt: Ob jemand einen Faktor eingerichtet hat,
 * steht zwar nicht darin -- aber der Aufruf ist der einzige Anlass, bei dem
 * sich am Zugang gerade etwas geaendert hat, und eine Ansicht, die dann
 * veraltet stehenbleibt, laedt zum zweiten Klick ein.
 */
export function useResetMfa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => resetMfaFor(userId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: userKeys.list });
    },
  });
}

/**
 * Legt einen Mitarbeiter an -- F0-06.
 *
 * Die Antwort traegt das Erstkennwort. Es steht dort **einmal** und danach
 * nirgends mehr; der Aufrufer zeigt es an und speichert es nicht.
 */
export function useCreateAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { email: string; full_name: string; role_id?: string }) =>
      request<AgentCreated>('/users', { method: 'POST', body: payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: userKeys.list });
    },
  });
}

/** Name, Rolle und Zustand eines Mitarbeiters. */
export function useUpdateAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: string;
      payload: { full_name?: string; is_active?: boolean; role_id?: string };
    }) => request<AssignableUser>(`/users/${userId}`, { method: 'PATCH', body: payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: userKeys.list });
      // Die Traegerzahl unter "Rollen" haengt an der Zuweisung.
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

/** Vergibt ein neues Erstkennwort -- der Weg fuer das vergessene Kennwort. */
export function useResetAgentPassword() {
  return useMutation({
    mutationFn: (userId: string) =>
      request<PasswordReset>(`/users/${userId}/password`, { method: 'POST' }),
  });
}
